import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { RoleBadge } from './RoleBadge';
import { cn } from '@/lib/utils';
import { X, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface CompactRoleSelectorProps {
  selectedRoles: string[] | null | undefined;
  onChange: (roles: string[] | null) => void;
  label?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
}

const AVAILABLE_ROLES = ['operator', 'quality', 'site_manager'];

/**
 * CompactRoleSelector - A space-efficient role selector with dropdown
 * Shows selected roles as removable badges, with a dropdown to add more roles
 * Used in the step editor where space is limited
 */
export function CompactRoleSelector({
  selectedRoles,
  onChange,
  label,
  description,
  className,
  disabled = false,
}: CompactRoleSelectorProps) {
  const { t } = useTranslation();

  // Handle adding a role from the dropdown
  const handleAddRole = (role: string) => {
    const currentRoles = selectedRoles || [];
    if (!currentRoles.includes(role)) {
      const newRoles = [...currentRoles, role];
      onChange(newRoles);
    }
  };

  // Handle removing a role badge
  const handleRemoveRole = (role: string) => {
    const currentRoles = selectedRoles || [];
    const newRoles = currentRoles.filter((r) => r !== role);
    // If no roles left, set to null (means anyone can access)
    onChange(newRoles.length > 0 ? newRoles : null);
  };

  // Get roles that can still be added (not already selected)
  const availableToAdd = AVAILABLE_ROLES.filter(
    (role) => !selectedRoles?.includes(role)
  );

  const hasNoRoles = !selectedRoles || selectedRoles.length === 0;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">{label}</Label>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {/* Display selected roles as removable badges */}
        {selectedRoles && selectedRoles.length > 0 ? (
          selectedRoles.map((role) => (
            <div key={role} className="relative group">
              <RoleBadge role={role} size="sm" />
              {!disabled && (
                <button
                  onClick={() => handleRemoveRole(role)}
                  className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label={`Remove ${role}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))
        ) : (
          <Badge variant="outline" className="text-xs">
            {t('roles.noRolesSelected')}
          </Badge>
        )}

        {/* Dropdown to add more roles */}
        {availableToAdd.length > 0 && !disabled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('editor.addRole')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {availableToAdd.map((role) => (
                <DropdownMenuItem
                  key={role}
                  onClick={() => handleAddRole(role)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <RoleBadge role={role} size="sm" />
                    <span className="text-xs text-muted-foreground">
                      {t(`roles.${role}.description`)}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
